import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import CountryFlag from 'components/base/CountryFlag';
import CustomerIcon from 'components/base/CustomerIcon/CustomerIcon';
import Box from 'components/elements/Box';
import Button from 'components/elements/button';
import Span from 'components/elements/span';
import ChannelDropdown from './ChannelDropdown';
import { FlagImage } from 'components/base/TopicFiltersDropdown/RegionDropdown/index';

import { ReactComponent as Trashbin } from 'icons/icon-trashbin.svg';
import { ReactComponent as CustomizeIcon } from 'icons/icon-customize.svg';
import { ReactComponent as regionIcon } from 'icons/icon-region.svg';

import {
  ISubscription,
  removeSubscription,
  EventCategories,
  IEventChannel,
  ChannelTypes,
} from 'redux/reducers/subscriptions';

import useCheckboxes from './utils/useCheckboxes';
import useSettings from './utils/useSettings';
import getSubscriptionForView from './utils/getSubscriptionForView';

import { itemToString } from 'components/notifications/Settings/CreateSubscription';
import {
  channelTypes,
  channelTypeNames,
  channelTypeAllNames,
  channelPriorityAndChartsTypeNames,
  channelPriorityAndChartsEditTypeNames,
  eventCategoryLabels,
} from './typeToName';
import { ILabelValuePair } from 'types/shared';

import { CategoryDefinitionLabel, CustomerBox, MobileButton } from './styles';
import colors from 'styles/themes/colors';

import { SHOW_BELL_NOTIFICATION } from 'config';

const SubscriptionSummary: React.FC<ISubscriptionSummaryProps> = (props) => {
  const {
    subscription,
    countries,
    customers,
    loading,
    regions,
    isLargeScreen,
    isOpusNotifierOn,
  } = props;
  const global = { id: 'WW', displayName: 'Global' };
  const allCustomers = { id: 'all', displayName: 'All DSPs' };
  const country = countries.find(isSameCountryId) || global;
  const customer = customers.find(isSameCustomerId) || allCustomers;
  const dispatch = useDispatch();

  const subscriptionForView = getSubscriptionForView(subscription);
  const [currentSettings, save, cancel, , setCurrentSettings] = useSettings(
    subscriptionForView,
    subscriptionForView.entityId
  );
  const [handleChecked, getIsSelected] = useCheckboxes(
    currentSettings,
    setCurrentSettings
  );

  const [isEditing, setIsEditing] = useState(false);
  const [hasNoSelectedCheckboxes, setHasNoSelectedCheckboxes] = useState(false);

  useEffect(() => {
    setCurrentSettings(subscription);
  }, [subscription, setCurrentSettings]);

  function isSameCountryId(country: any): boolean {
    return country.id === subscription.countryId;
  }

  function isSameCustomerId(customer: any): boolean {
    return customer.id === subscription.customerId;
  }

  function deleteSubscription(): void {
    if (subscription.id && !loading) {
      dispatch(removeSubscription(subscription.id));
    }
  }

  function updateSubscription(): void {
    if (!loading) {
      if (currentSettings.eventChannels.length === 0) {
        setHasNoSelectedCheckboxes(true);
      } else {
        save();
        setIsEditing(false);
        setHasNoSelectedCheckboxes(false);
      }
    }
  }
  const handleCheckedInput =
    (eventCategory: EventCategories): any =>
    ({ value }: ILabelValuePair<ChannelTypes | 'all'>): void => {
      const alreadySelected = getIsSelected(eventCategory, value);
      const channels =
        eventCategory === 'priority' || eventCategory === 'charts'
          ? channelPriorityAndChartsEditTypeNames(eventCategory)
          : channelTypeNames;
      /** If we select 'Select all', we need to check/uncheck all the other items */
      if (value === 'all') {
        [...Object.keys(channels), 'all'].forEach((channel: ChannelTypes) => {
          handleChecked(channel, eventCategory, alreadySelected);
        });
        setHasNoSelectedCheckboxes(alreadySelected);
      } else {
        const selected = currentSettings.eventChannels
          .filter((channel: any) => channel.channelId !== 'all')
          .filter((channel: any) =>
            channel?.eventCategories?.includes(eventCategory)
          );
        /** If we select all the items, we need to check 'Select all' option */
        if (eventCategory === 'priority') {
          if (
            selected.length === Object.keys(channels).length &&
            !alreadySelected
          ) {
            handleChecked('all' as ChannelTypes, eventCategory, false, true);
          }
          if (selected.length === Object.keys(channels).length) {
            handleChecked('all' as ChannelTypes, eventCategory, true, true);
          }
        } else {
          if (selected.length === 2 && !alreadySelected) {
            handleChecked('all' as ChannelTypes, eventCategory, false, true);
          }
          if (currentSettings.eventChannels.length === 4) {
            handleChecked('all' as ChannelTypes, eventCategory, true, true);
          }
        }
        handleChecked(
          value as ChannelTypes,
          eventCategory,
          alreadySelected,
          true
        );
        setHasNoSelectedCheckboxes(false);
      }
      if (currentSettings.eventChannels.length <= 0)
        setHasNoSelectedCheckboxes(true);
    };

  const isRegion = subscription.countryId?.startsWith('RE-');
  const subscribedRegion = regions.find(
    (region: any) => region.id === subscription.countryId
  );

  const listChannelTypes = (): any => {
    if (SHOW_BELL_NOTIFICATION) {
      return [
        { label: 'OPUS website notifications', value: 'on_site' },
        { label: 'Daily e-mail', value: 'email_daily_digest' },
        { label: 'Weekly e-mail', value: 'email_weekly_digest' },
      ];
    } else {
      return [
        { label: 'Daily e-mail', value: 'email_daily_digest' },
        { label: 'Weekly e-mail', value: 'email_weekly_digest' },
      ];
    }
  };

  const listChannelTypesPriorityAndCharts = (event: EventCategories): any => {
    if (SHOW_BELL_NOTIFICATION) {
      return [
        { label: 'OPUS website notifications', value: 'on_site' },
        { label: 'E-mail immediately', value: 'standalone_email' },
        ...(event === 'priority' && isOpusNotifierOn
          ? [{ label: 'Mobile alert immediately', value: 'push' }]
          : []),
      ];
    } else {
      return [{ label: 'E-mail immediately', value: 'standalone_email' }];
    }
  };

  return (
    <Box mx={0} my={4} bg="greys.6">
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={isLargeScreen ? '33px' : '25px'}
        py={5}
        borderBottom={isLargeScreen ? '' : `1px solid ${colors.LightGrey}`}
      >
        <Box>
          <Box display="flex" alignItems="center" flexWrap="wrap">
            <Box>
              {isRegion ? (
                <Box display="flex" alignItems="center">
                  <Box alignSelf="auto" display="flex">
                    <FlagImage as={regionIcon} width={19} height={19} />
                  </Box>
                  <Span fontWeight={600}>{subscribedRegion?.displayName}</Span>
                </Box>
              ) : (
                <>
                  <CountryFlag small {...country} />
                  <Span fontWeight={600}>{country.displayName}</Span>
                </>
              )}
            </Box>

            <Span marginRight={2}>,</Span>

            <CustomerBox display="flex">
              <CustomerIcon small customer={customer.displayName} />
              <Span fontWeight={600}>{customer.displayName}</Span>
            </CustomerBox>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" mb={isLargeScreen ? 4 : 0}>
          <Button
            data-gtag={'notifications-modal-unsubscribe-single-subscription'}
            type="button"
            display={['none', 'flex']}
            alignItems="center"
            justifyContent="center"
            mx={1}
            variant={loading ? 'disabled' : 'secondary'}
            onClick={deleteSubscription}
            minWidth="6em"
          >
            <Trashbin />
            Delete
          </Button>

          {isLargeScreen ? (
            <Button
              type="button"
              display="flex"
              alignItems="center"
              justifyContent="center"
              mx={1}
              variant={loading ? 'disabled' : 'secondary'}
              minWidth="6em"
              onClick={(): void => {
                if (!loading) setIsEditing(!isEditing);
                cancel();
              }}
            >
              {!isEditing && isLargeScreen && <CustomizeIcon />}
              {!isEditing ? 'Edit' : 'Cancel'}
            </Button>
          ) : (
            <MobileButton
              type="button"
              isUnderlined={!isEditing}
              onClick={(): void => {
                if (!loading) setIsEditing(!isEditing);
                cancel();
              }}
            >
              {!isEditing ? 'Edit' : 'Cancel'}
            </MobileButton>
          )}
        </Box>
      </Box>

      <Box px={isLargeScreen ? '33px' : '25px'} py={5}>
        {Object.keys(eventCategoryLabels).map(
          (category: EventCategories, index: number) => {
            const shouldCategoryDisplayed = subscription.eventChannels.some(
              (eventChannel: IEventChannel) =>
                eventChannel.eventCategories.some((channel: EventCategories) =>
                  channel.includes(category)
                )
            );
            const channels = currentSettings.eventChannels.reduce(
              (prev: ChannelTypes[], channel: IEventChannel) =>
                channel.eventCategories.includes(category)
                  ? [...prev, channel.channelId]
                  : prev,
              []
            );
            const selectedItemsOrdinary = channels.map(
              (channelId: ChannelTypes) => ({
                value: channelId,
                label: channelTypeAllNames[channelId],
              })
            );
            const subCategoryTitle =
              category === 'priority' ? 'Priority' : 'All';
            const hasPlaylists =
              category === 'playlisting' || category === 'priority';
            const items =
              category !== 'priority' && category !== 'charts'
                ? [{ label: 'Select all', value: 'all' }, ...listChannelTypes()]
                : [
                    { label: 'Select all', value: 'all' },
                    ...listChannelTypesPriorityAndCharts(category),
                  ];
            const selectedItemsPriorityAndCharts = channels
              .map((channelId: any) => {
                const channel = listChannelTypesPriorityAndCharts(
                  category
                ).find((ch: any) => channelId === ch.value);
                return channelId === 'all'
                  ? { value: channelId, label: 'Select all' }
                  : channel
                  ? {
                      value: channelId,
                      label: channel?.label,
                    }
                  : null;
              })
              .filter((channel: any) => channel !== null)
              .filter(
                (channel: any) =>
                  isEditing && channel.label !== 'In a weekly e-mail'
              );
            const selectedItems =
              category === 'priority' || category === 'charts'
                ? selectedItemsPriorityAndCharts?.length ===
                  Object.keys(channelPriorityAndChartsTypeNames(category))
                    .length
                  ? [
                      { label: 'Select all', value: 'all' },
                      ...selectedItemsPriorityAndCharts,
                    ]
                  : selectedItemsPriorityAndCharts
                : selectedItemsOrdinary?.length ===
                  Object.keys(channelTypeNames).length
                ? [
                    { label: 'Select all', value: 'all' },
                    ...selectedItemsOrdinary,
                  ]
                : selectedItemsOrdinary;
            const channelsLabels = channels
              .filter((item: any) => item !== 'all')
              .map((item: ChannelTypes) =>
                (category === 'priority' && item === 'email_weekly_digest') ||
                (category === 'priority' &&
                  item === 'push' &&
                  !isOpusNotifierOn)
                  ? null
                  : category === 'priority' && item === 'email_daily_digest'
                  ? 'via immediate email'
                  : SHOW_BELL_NOTIFICATION &&
                    category === 'priority' &&
                    item === 'push'
                  ? 'via immediate mobile alert'
                  : channelTypes[item]
              )
              .filter((channel: any) => channel !== null)
              .join(', ');

            return (
              <React.Fragment key={index}>
                {shouldCategoryDisplayed && (
                  <>
                    <Box
                      display="grid"
                      gridGap={isLargeScreen ? 2 : 0}
                      gridTemplateColumns={isLargeScreen ? '1fr 2fr' : '1fr'}
                      alignItems="center"
                      mt={4}
                      mb={isLargeScreen ? 4 : 5}
                    >
                      <CategoryDefinitionLabel>
                        {hasPlaylists
                          ? 'Playlist alerts'
                          : eventCategoryLabels[category]}
                        {hasPlaylists && (
                          <Span fontSize="11px">{` (${subCategoryTitle})`}</Span>
                        )}
                      </CategoryDefinitionLabel>
                      {!isEditing && <Span>{channelsLabels}</Span>}
                      {isEditing && (
                        <ChannelDropdown
                          defaultLabel="Send it..."
                          items={items}
                          itemToString={itemToString}
                          onChange={handleCheckedInput(category)}
                          selectedItems={selectedItems}
                        />
                      )}
                    </Box>
                  </>
                )}
              </React.Fragment>
            );
          }
        )}

        {isEditing && (
          <Box
            display="flex"
            justifyContent="flex-end"
            flexDirection={isLargeScreen ? 'row' : 'column'}
          >
            <Button
              data-pw="notifications-modal-edit"
              data-gtag={'notifications-modal-edit'}
              variant={
                loading
                  ? 'disabled'
                  : hasNoSelectedCheckboxes
                  ? 'disablednotification'
                  : 'primary'
              }
              onClick={updateSubscription}
              type="button"
              fontSize="16px"
              fontWeight="600"
              py={2}
              px={5}
              width={isLargeScreen ? 'auto' : '100%'}
            >
              Update {!isLargeScreen && 'notification'}
            </Button>

            {!isLargeScreen && (
              <Button
                data-gtag={
                  'notifications-modal-unsubscribe-single-subscription'
                }
                type="button"
                display="flex"
                alignItems="center"
                justifyContent="center"
                mx={isLargeScreen ? 1 : 'auto'}
                mt={!isLargeScreen && 2}
                variant={loading ? 'disabled' : 'secondary'}
                onClick={deleteSubscription}
                minWidth="6em"
              >
                <Trashbin />
                Delete
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

interface ISubscriptionSummaryProps {
  subscription: ISubscription;
  customers: any;
  countries: any;
  loading: boolean;
  regions: any;
  isLargeScreen: boolean;
  isOpusNotifierOn: boolean | undefined;
}

export default SubscriptionSummary;
